import './prism-languages';

declare const $: any;

export interface CodeBlockOptions {
  /** Code HTML (as rendered by Prism) used for the copy value */
  value: string;
  /** Tooltip / label of the copy button */
  copyTitle: string;
  /** Tooltip / label of the full screen button */
  fullScreenTitle: string;
  /** Show the paste icon and paste into the VM instead of copying */
  isPaste?: boolean;
  /** Called with the block (wrapper) element when full screen is clicked */
  onFullScreen: (block: HTMLElement) => void;
}

/**
 * Plain text to copy: parse the code HTML and drop Prism's line number markup.
 * DOMParser does not run scripts or inline handlers of the parsed content.
 */
export function getCodeCopyText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc
    .querySelectorAll('.line-numbers-rows, .line-numbers-sizer')
    .forEach((el) => el.remove());
  return (doc.body.textContent || '').trimEnd();
}

/** Display names for markdown fence languages (```yaml, ```ps1, ...) */
const LANGUAGE_NAMES: { [alias: string]: string } = {
  bash: 'Bash', sh: 'Bash', shell: 'Bash', zsh: 'Bash', console: 'Bash', terminal: 'Bash',
  powershell: 'PowerShell', ps: 'PowerShell', ps1: 'PowerShell', pwsh: 'PowerShell',
  cmd: 'CMD', bat: 'CMD', batch: 'CMD',
  yaml: 'YAML', yml: 'YAML',
  json: 'JSON', jsonc: 'JSON',
  xml: 'XML', html: 'HTML', css: 'CSS', scss: 'SCSS',
  javascript: 'JavaScript', js: 'JavaScript', typescript: 'TypeScript', ts: 'TypeScript',
  python: 'Python', py: 'Python',
  csharp: 'C#', cs: 'C#', 'c#': 'C#', java: 'Java', go: 'Go', ruby: 'Ruby', php: 'PHP',
  sql: 'SQL', tsql: 'SQL', kql: 'KQL', kusto: 'KQL',
  dockerfile: 'Dockerfile', docker: 'Dockerfile',
  terraform: 'Terraform', tf: 'Terraform', hcl: 'HCL', bicep: 'Bicep',
  azurecli: 'Azure CLI', 'azure-cli': 'Azure CLI',
  text: 'Text', txt: 'Text', plaintext: 'Text',
};

/** Prism grammar for each header label (see prism-languages.ts) */
const PRISM_LANGUAGES: { [label: string]: string } = {
  Bash: 'bash', 'Azure CLI': 'bash', PowerShell: 'powershell', CMD: 'batch',
  YAML: 'yaml', JSON: 'json', XML: 'markup', HTML: 'markup', CSS: 'css', SCSS: 'scss',
  JavaScript: 'javascript', TypeScript: 'typescript', Python: 'python', 'C#': 'csharp',
  Java: 'java', Go: 'go', Ruby: 'ruby', PHP: 'php', SQL: 'sql', KQL: 'kusto',
  Dockerfile: 'docker', Terraform: 'hcl', HCL: 'hcl', Bicep: 'bicep',
};

/**
 * Colors the code with Prism for the given header label (or the raw fence language).
 * The guide renderers emit "language-none", so the block is switched to the matching
 * "language-*" class and re-highlighted; Prism's line numbers plugin re-adds the numbers.
 */
function highlightCodeBlock(pre: HTMLElement, label: string, fenceLang: string): void {
  const prism = (window as any).Prism;
  const code = pre.querySelector('code');
  const lang = PRISM_LANGUAGES[label] || fenceLang;
  if (!prism?.highlightElement || !code || !lang || !prism.languages[lang]) {
    return;
  }
  // Already colored (re-renders call this again); re-color only if the content was replaced
  if (code.classList.contains(`language-${lang}`) && code.querySelector('.token')) {
    return;
  }
  [pre, code].forEach((el) => {
    el.className = el.className.replace(/\blanguage-[\w-]+/g, '').trim();
    el.classList.add(`language-${lang}`);
  });
  prism.highlightElement(code);
}

/** Shell command words that start a command line */
const SHELL_COMMAND =
  /^\s*(\$\s+)?(sudo|apt(-get)?|yum|dnf|brew|az|azd|kubectl|helm|docker|git|npm|npx|yarn|pnpm|node|curl|wget|cd|ls|cat|echo|export|source|chmod|chown|mkdir|rm|cp|mv|touch|grep|sed|awk|ssh|scp|func|dotnet|pip3?|python3?|terraform|gcloud|aws|mvn|gradle|make|systemctl|service|tar|unzip|code)\b/;

/** Language set on the markdown code fence (```yaml), stored by the guide renderers in `data-lang` */
export function getFenceLanguage(infostring: string): string {
  // Only the first word of the info string, limited to safe characters
  const lang = (infostring || '').trim().split(/\s+/)[0].toLowerCase();
  return /^[\w#+.-]{1,30}$/.test(lang) && lang !== 'none' ? lang : '';
}

/**
 * Label for the code block header: the fence language when the author set one,
 * otherwise a best guess from the code itself ("Code" when nothing matches).
 */
export function detectCodeLanguage(code: string, fenceLang?: string): string {
  if (fenceLang) {
    return LANGUAGE_NAMES[fenceLang] || fenceLang.toUpperCase();
  }
  const text = (code || '').trim();
  if (!text) {
    return 'Code';
  }
  // Non-empty lines, ignoring "#" comments
  const lines = text.split(/\r?\n/).filter((l) => l.trim() && !/^\s*#/.test(l));

  if (/^[[{]/.test(text)) {
    try {
      JSON.parse(text);
      return 'JSON';
    } catch {
      // not JSON
    }
  }
  if (/^<\?xml/i.test(text)) {
    return 'XML';
  }
  if (/^<(!doctype html|html|head|body|div|span|p|a|script|style)\b/i.test(text)) {
    return 'HTML';
  }
  if (/^</.test(text) && />$/.test(text)) {
    return 'XML';
  }
  if (/^#!.*\b(bash|sh|zsh)\b/.test(text)) {
    return 'Bash';
  }
  if (
    /\b(Get|Set|New|Remove|Install|Import|Connect|Invoke|Start|Stop|Add|Update|Write|Test|Select|Where|ForEach|Out|Read|Restart|Enable|Disable|Register|Expand|Copy|Move)-[A-Z][A-Za-z]+\b/.test(text) ||
    /\$env:\w+|-ErrorAction\b|\[System\.\w+|\$\w+\s*=\s*@?[({]/.test(text)
  ) {
    return 'PowerShell';
  }
  if (
    /^\s*(def|class)\s+\w+.*:\s*$/m.test(text) ||
    /^\s*from\s+[\w.]+\s+import\s+/m.test(text) ||
    (/^\s*import\s+[\w.]+\s*$/m.test(text) && !/[;{}]/.test(text))
  ) {
    return 'Python';
  }
  if (
    /^\s*(SELECT\b[\s\S]+\bFROM|INSERT\s+INTO|UPDATE\s+\w+\s+SET|DELETE\s+FROM|CREATE\s+(TABLE|DATABASE|VIEW|INDEX|PROCEDURE)|ALTER\s+TABLE|DROP\s+(TABLE|DATABASE))\b/im.test(text)
  ) {
    return 'SQL';
  }
  if (/^FROM\s+\S+/m.test(text) && /^(RUN|COPY|CMD|ENTRYPOINT|WORKDIR|EXPOSE|ENV)\s/m.test(text)) {
    return 'Dockerfile';
  }
  if (
    /\b(const|let|var)\s+\w+\s*=|\bfunction\s*\w*\s*\(|=>|console\.log\(|require\(['"]/.test(text) &&
    /[;{}()]/.test(text)
  ) {
    return 'JavaScript';
  }
  // YAML: mostly "key: value" / "- item" lines and no code punctuation at line ends
  const yamlLines = lines.filter(
    (l) => /^\s*(-\s+)?[\w"'./-]+:(\s|$)/.test(l) || /^\s*-\s+\S/.test(l)
  );
  if (
    /^---\s*$/m.test(text) ||
    (yamlLines.length >= 2 && yamlLines.length >= lines.length * 0.6 && !/[;{}]\s*$/m.test(text))
  ) {
    return 'YAML';
  }
  // Shell: a known command word, or command-like lines that use flags / pipes
  const commandLike = (l: string) =>
    /^\s*[a-z][\w.-]*(\s+\S+)+\s*\\?$/.test(l) || /^\s*[A-Z_][A-Z0-9_]*=/.test(l);
  if (
    lines.some((l) => SHELL_COMMAND.test(l)) ||
    (lines.every(commandLike) && /(^|\s)-{1,2}[A-Za-z][\w-]*|\s\|\s/.test(text))
  ) {
    return 'Bash';
  }
  return 'Code';
}

/**
 * Wraps a guide `<pre>` in a code block with a header (language label, full screen and copy buttons).
 * Safe to call on every render: an already wrapped block only gets its copy value / label refreshed.
 * Copy / paste clicks are handled by the existing document listeners on `.copyelement`.
 */
export function enhanceCodeBlock(pre: HTMLElement, options: CodeBlockOptions): void {
  const copyText = getCodeCopyText(options.value);
  const fenceLang = pre.getAttribute('data-lang') || '';
  const language = detectCodeLanguage(copyText, fenceLang);
  highlightCodeBlock(pre, language, fenceLang);
  const wrapper = $(pre).parent('.code-block-wrapper');
  if (wrapper.length) {
    wrapper.find('.copyelement p').text(copyText);
    wrapper.find('.code-block-lang').text(language);
    return;
  }
  $(pre).wrap('<div class="code-block-wrapper"></div>');
  const header = $(
    `<div class="code-block-header">
      <span class="code-block-lang"></span>
      <div class="code-block-actions">
        <a tabindex="0" role="button" class="code-block-btn code-block-fullscreen" data-toggle="tooltip"><i class="fa-solid fa-expand" aria-hidden="true"></i></a>
        <a tabindex="0" role="button" data-toggle="tooltip" alt="" id="${
          options.isPaste ? 'pasteInsideVM' : ''
        }" class="code-block-btn fa-regular ${
          options.isPaste ? 'fa-paste' : 'fa-clone'
        } copyelement"><p style="display:none"></p></a>
      </div>
    </div>`
  );
  // Set via attr()/text() so the values are not parsed as HTML
  header
    .find('.code-block-fullscreen')
    .attr({ title: options.fullScreenTitle, 'aria-label': options.fullScreenTitle });
  header
    .find('.copyelement')
    .attr({ title: options.copyTitle, 'aria-label': options.copyTitle });
  header.find('.copyelement p').text(copyText);
  header.find('.code-block-lang').text(language);
  $(pre).before(header);

  header
    .find('.code-block-fullscreen')
    .on('click', function () {
      $(this).tooltip('hide');
      options.onFullScreen($(this).closest('.code-block-wrapper')[0]);
    })
    .on('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        $(this).trigger('click');
      }
    });
  // Clicking the header (outside the buttons) focuses the block
  header.on('click', (e) => {
    if (!$(e.target).closest('.code-block-btn').length) {
      $(pre).trigger('focus');
    }
  });
  // Show the focus ring on the whole block instead of only the code area
  $(pre)
    .on('focus', () => $(pre).parent('.code-block-wrapper').addClass('is-focused'))
    .on('blur', () => $(pre).parent('.code-block-wrapper').removeClass('is-focused'));
  header.find('[data-toggle="tooltip"]').tooltip({
    placement: 'bottom',
    trigger: 'hover',
  });
}
